"use client";
import { useEffect, useRef } from "react";
import { Animated, StyleSheet } from "react-native";

import Token from "../Token/Token";
import View from "../View/View";

type Props = {
  active: boolean;
  dimension?: {
    topSpace: number;
    leftSpace: number;
    height: number;
    width: number;
  };
};

export default function HighlightElement(props: Props) {
  const { active, dimension } = props;
  const animatedValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (active) {
      // Start the continuous animation when active
      Animated.loop(
        Animated.timing(animatedValue, {
          toValue: 1,
          duration: 750,
          useNativeDriver: false,
        })
      ).start();
    } else {
      // Stop animation and reset when inactive
      animatedValue.stopAnimation();
      animatedValue.setValue(0);
    }
  }, [active, animatedValue]);

  if (!dimension) return null;

  const dashSize = 8;
  const gapSize = 4;
  const dashPattern = dashSize + gapSize;

  // Calculate how many dashes fit on each side
  const topDashes = Math.ceil(dimension.width / dashPattern);
  const rightDashes = Math.ceil(dimension.height / dashPattern);
  const bottomDashes = Math.ceil(dimension.width / dashPattern);
  const leftDashes = Math.ceil(dimension.height / dashPattern);

  const renderDashes = (
    side: "top" | "right" | "bottom" | "left",
    count: number
  ) => {
    const dashes = [];

    for (let i = 0; i < count + 1; i++) {
      // +1 to ensure coverage during animation
      const basePosition = i * dashPattern;

      let style;
      if (side === "top") {
        style = {
          position: "absolute" as const,
          top: 0,
          left: animatedValue.interpolate({
            inputRange: [0, 1],
            outputRange: [basePosition - dashPattern, basePosition],
          }),
          width: dashSize,
          height: 2,
          backgroundColor: Token.color.highAltitude,
        };
      } else if (side === "right") {
        style = {
          position: "absolute" as const,
          right: 0,
          top: animatedValue.interpolate({
            inputRange: [0, 1],
            outputRange: [basePosition - dashPattern, basePosition],
          }),
          width: 2,
          height: dashSize,
          backgroundColor: Token.color.highAltitude,
        };
      } else if (side === "bottom") {
        style = {
          position: "absolute" as const,
          bottom: 0,
          right: animatedValue.interpolate({
            inputRange: [0, 1],
            outputRange: [basePosition - dashPattern, basePosition],
          }),
          width: dashSize,
          height: 2,
          backgroundColor: Token.color.highAltitude,
        };
      } else {
        // left
        style = {
          position: "absolute" as const,
          left: 0,
          bottom: animatedValue.interpolate({
            inputRange: [0, 1],
            outputRange: [basePosition - dashPattern, basePosition],
          }),
          width: 2,
          height: dashSize,
          backgroundColor: Token.color.highAltitude,
        };
      }

      dashes.push(<Animated.View key={`${side}-${i}`} style={style} />);
    }
    return dashes;
  };

  return (
    <View
      style={[
        styles.highlightBorder,
        active && styles.active,
        {
          top: dimension.topSpace,
          left: dimension.leftSpace,
          height: dimension.height,
          width: dimension.width,
        },
      ]}
    >
      {/* Render animated dashes on all sides */}
      {active && (
        <View style={styles.dashContainer}>
          {/* Top dashes */}
          {renderDashes("top", topDashes)}

          {/* Right dashes */}
          {renderDashes("right", rightDashes)}

          {/* Bottom dashes */}
          {renderDashes("bottom", bottomDashes)}

          {/* Left dashes */}
          {renderDashes("left", leftDashes)}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  highlightBorder: {
    position: "absolute",
    zIndex: 1001,
    borderRadius: Token.borderRadius.normal,
    opacity: 0,
  },
  active: {
    opacity: 1,
  },
  dashContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "hidden", // Hide dashes that go outside bounds
  },
});
